import { useTranslation } from "react-i18next";
import { BaseAssignmentResponseDto } from "@packages/api/madlenAPI.schemas";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Calendar,
  Clock,
  FileText,
  Pencil,
  Share,
  GraduationCap,
} from "lucide-react";
import { useState } from "react";
import { formatDate } from "@packages/utils";
import { Button, Text } from "../../../core";
import { Input } from "@core/input";
import AssignmentConstraints from "./AssignmentConstraints";
import { CourseData } from "@packages/constant/CourseData";
import { useToast } from "@packages/contexts/ToastContext";

const AssignmentCard = ({
  assignment,
  onUpdateName,
}: {
  assignment: BaseAssignmentResponseDto;
  onUpdateName: (id: number, name: string) => void;
}) => {
  const { t, i18n } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(assignment.name ?? "");
  const navigate = useNavigate();
  const { addToast } = useToast();

  const handleEditClick = () => {
    setEditedName(assignment.name);
    setIsEditing(true);
  };

  const handleSaveName = () => {
    if (assignment.id && onUpdateName) {
      onUpdateName(assignment.id, editedName);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveName();
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setEditedName(assignment.name);
    }
  };

  const handleShare = () => {
    if (assignment.id) {
      const link = `${window.location.origin}/clone-assignment/${assignment.id}`;
      navigator.clipboard.writeText(link);
      addToast({
        title: t("assignments.card.shareTitle"),
        description: t("assignments.card.shareDescription"),
        variant: "success",
        duration: 3500,
      });
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col min-h-[300px] overflow-hidden">
      <div className="relative">
        {assignment.assignmentImageUrl && (
          <div className="w-full h-40 overflow-hidden bg-gray-100 dark:bg-gray-700">
            <img
              src={assignment.assignmentImageUrl}
              alt={t("assignments.image.alt")}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
          </div>
        )}

        <div className="absolute top-2 right-2">
          <Text
            variant="span"
            className={`px-3 py-1 rounded-full text-sm font-medium shadow-sm
            ${
              assignment.status === "ASSIGNED"
                ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100"
                : assignment.status === "DRAFT"
                  ? "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                  : assignment.status === "FAILED"
                    ? "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100"
                    : "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100"
            }`}
          >
            {t(`assignments.filters.${assignment.status.toLowerCase()}`)}
          </Text>
        </div>
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <div className="mb-4">
          {isEditing ? (
            <Input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={handleKeyDown}
              classNames={{
                input:
                  "w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100",
              }}
              autoFocus
              maxLength={100}
            />
          ) : (
            <div className="flex items-center gap-2">
              <Text variant="h3" className="font-medium line-clamp-2">
                {assignment.name}
              </Text>
              <Button
                onClick={handleEditClick}
                size="xs"
                rounded="full"
                bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                title={t("lessonPlanner.actions.edit")}
              >
                <Pencil
                  size={16}
                  className="text-gray-500 dark:text-gray-400"
                />
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-4">
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <Calendar size={16} className="mr-2 flex-shrink-0" />
            <span className="truncate">
              {t("assignments.card.due")}:{" "}
              {assignment.endDate
                ? formatDate(assignment.endDate, i18n.language)
                : "-"}
            </span>
          </div>
          {assignment.type === "QUIZ" && (
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Clock size={16} className="mr-2 flex-shrink-0" />
              <span className="truncate">
                {(() => {
                  if (!assignment.duration) return "-";
                  const diffMs = assignment.duration * 60 * 1000;

                  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                  const hours = Math.floor(
                    (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
                  );
                  const minutes = Math.floor(
                    (diffMs % (1000 * 60 * 60)) / (1000 * 60),
                  );

                  const parts = [];
                  if (days > 0) {
                    parts.push(`${days} ${t("common.time.days")}`);
                  }
                  if (hours > 0) {
                    parts.push(`${hours} ${t("common.time.hours")}`);
                  }
                  if (minutes > 0) {
                    parts.push(`${minutes} ${t("common.time.minutes")}`);
                  }

                  return parts.length > 0
                    ? parts.join(" ")
                    : "0 " + t("common.time.minutes");
                })()}
              </span>
            </div>
          )}
          {assignment.grade && (
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <GraduationCap size={16} className="mr-2 flex-shrink-0" />
              <span className="truncate">
                {t("assignments.grade", { grade: assignment.grade })}
              </span>
            </div>
          )}
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <BookOpen size={16} className="mr-2 flex-shrink-0" />
            <span className="truncate">
              {t(
                "courseType." +
                  CourseData[assignment.courseType]?.name(
                    assignment.grade ?? 8,
                  ),
              )}
            </span>
          </div>
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <FileText size={16} className="mr-2 flex-shrink-0" />
            <span className="truncate">{t(assignment.type)}</span>
          </div>
        </div>

        <div className="flex-1 pt-3 mb-4 border-t dark:border-gray-700">
          <AssignmentConstraints assignment={assignment} />
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            bgColor="bg-primary-50 hover:bg-primary-100 dark:bg-gray-700 dark:hover:bg-gray-600"
            textColor="text-primary-700 dark:text-gray-100"
            rounded="lg"
            className="flex-1 transition-all"
            onClick={() => navigate(`/assignments/${assignment.id}`)}
          >
            {t("assignments.card.viewDetails")}
          </Button>
          <Button
            size="sm"
            rounded="lg"
            bgColor="bg-primary-50 hover:bg-primary-100 dark:bg-gray-700 dark:hover:bg-gray-600"
            textColor="text-primary-700 dark:text-gray-100"
            className="transition-all"
            onClick={handleShare}
            title={t("common.share")}
          >
            <Share size={16} className="text-primary-600 dark:text-gray-100" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentCard;
